
// ArticleSidebar module
// Finds .js-ArticleSidebar lists, maps anchor items to page targets and
// toggles `is-active` on the anchor whose target is visible in the viewport.
// Also marks the last anchor `is-active` when user reached the bottom of the page.

const OFFSET = 150; // pixels to offset for sticky top bar

function isElementInViewport(el) {
	if (!el) return false;
	const r = el.getBoundingClientRect();
	const vh = (window.innerHeight || document.documentElement.clientHeight);
	// Element is considered visible if it appears within viewport area below the offset
	return (r.top <= (vh - OFFSET)) && (r.bottom > OFFSET);
}

function atPageBottom() {
	// Consider near-bottom as bottom too (2px tolerance)
	return (window.innerHeight + window.pageYOffset) >= (document.documentElement.scrollHeight - 2);
}

function initSidebar(sidebar) {
	if (!sidebar) return;

	const links = Array.from(sidebar.querySelectorAll('a.article__item'));
	if (!links.length) return;

	const items = links.map(link => {
		const href = link.getAttribute('href') || '';
		if (href.charAt(0) === '#') {
			const id = href.slice(1);
			const target = document.getElementById(id);
			return { link, target, top: Infinity };
		}
		return { link, target: null, top: Infinity };
	});

	let ticking = false;

	function clearActive() {
		links.forEach(l => l.classList.remove('is-active'));
	}

	function setActiveLink(link) {
		if (!link) return;
		clearActive();
		link.classList.add('is-active');
	}

	function update() {
		ticking = false;

		// If scrolled to bottom, set last link active
		if (atPageBottom()) {
			const last = links[links.length - 1];
			setActiveLink(last);
			return;
		}

		// Use precomputed tops to find the last section above current scroll position
		const currentPos = window.pageYOffset + OFFSET + 1;
		// Build sorted list of items that have valid top
		const visibleItems = items.filter(it => isFinite(it.top)).sort((a, b) => a.top - b.top);
		let found = null;
		for (let i = 0; i < visibleItems.length; i++) {
			if (visibleItems[i].top <= currentPos) {
				found = visibleItems[i];
			} else {
				break;
			}
		}
		if (found) {
			setActiveLink(found.link);
			return;
		}

		// If none matched, clear active
		clearActive();
	}

	function updatePositions() {
		items.forEach(it => {
			if (it.target) {
				const rect = it.target.getBoundingClientRect();
				it.top = window.pageYOffset + rect.top;
			} else {
				it.top = Infinity;
			}
		});
	}

	function onScroll() {
		if (!ticking) {
			ticking = true;
			requestAnimationFrame(update);
		}
	}

	// Smooth scroll on click and set active immediately
	links.forEach(link => {
		link.addEventListener('click', function (e) {
			const href = this.getAttribute('href') || '';
			if (href.charAt(0) === '#') {
				const id = href.slice(1);
				const target = document.getElementById(id);
							if (target) {
								e.preventDefault();
								// compute target top with offset and smooth scroll
								const targetTop = window.pageYOffset + target.getBoundingClientRect().top - OFFSET;
								window.scrollTo({ top: targetTop, behavior: 'smooth' });
								// optimistic active set
								setActiveLink(this);
						// recalc positions and ensure eventual correct state after scroll ends
						updatePositions();
						setTimeout(() => update(), 350);
							}
			}
		}, { passive: false });
	});

	// Remove body class when any sidebar link is clicked (close article content)
	links.forEach(link => {
		link.addEventListener('click', function () {
			document.body.classList.remove('article-content-is-open');
		});
	});

	window.addEventListener('scroll', onScroll, { passive: true });
	window.addEventListener('resize', function () { updatePositions(); onScroll(); });
	window.addEventListener('load', function () { updatePositions(); onScroll(); });

	// init
	update();
}

export default function initArticleSidebar(context = document) {
	const sidebars = Array.from((context || document).querySelectorAll('.js-ArticleSidebar'));
	sidebars.forEach(initSidebar);
}

// Auto-init when DOM is ready
if (typeof document !== 'undefined') {
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', () => initArticleSidebar(document));
	} else {
		initArticleSidebar(document);
	}
}
